/** @jsxImportSource @emotion/react */
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import {
  ElementFieldConfig,
  GdalLayerInfo,
  buildElementConfig,
  createDefaultElementFieldConfig,
  getTypeCategory,
} from '@app/modules/Home/Data/types'
import { toast } from '@app/components/ui/Toast/toast'
import { uploadElementConfig } from '@app/services/dataUploadService'
import FieldConfigRow from './FieldConfigRow'

type Props = {
  gdalInfo: GdalLayerInfo | null
  gdalFailed: boolean
  gdalSkipped: boolean
  onReset: () => void
  datasetName: string
}

const ElementAttributesStep = ({
  gdalInfo,
  gdalFailed,
  gdalSkipped,
  onReset,
  datasetName,
}: Props) => {
  const [configs, setConfigs] = useState<ElementFieldConfig[]>([])
  const [showJson, setShowJson] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (gdalInfo) {
      setConfigs(gdalInfo.fields.map(createDefaultElementFieldConfig))
    }
  }, [gdalInfo])

  const updateConfig = (fieldName: string, updates: Partial<ElementFieldConfig>) => {
    setConfigs((prev) => prev.map((c) => (c.fieldName === fieldName ? { ...c, ...updates } : c)))
  }

  const generatedJson = JSON.stringify(buildElementConfig(configs), null, 2)

  const handleUploadConfig = async () => {
    setUploading(true)
    try {
      await uploadElementConfig(datasetName, buildElementConfig(configs))
      toast({ title: 'Config uploaded', type: 'success', position: 'top-right', duration: 3000 })
    } catch {
      toast({ title: 'Config upload failed', type: 'error', position: 'top-right', duration: 5000 })
    } finally {
      setUploading(false)
    }
  }

  const includedCount = configs.filter((c) => !c.ignore).length

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Layer summary */}
      {gdalInfo && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Layer: ${gdalInfo.layerName}`} size="small" variant="outlined" />
          <Chip label={`Geometry: ${gdalInfo.geometryType}`} size="small" variant="outlined" />
          <Chip
            label={`Features: ${gdalInfo.featureCount.toLocaleString()}`}
            size="small"
            variant="outlined"
          />
          {gdalInfo.crs && (
            <Chip
              label={`CRS: ${gdalInfo.crs.match(/"([^"]+)"/)?.[1] ?? gdalInfo.crs}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {gdalInfo?.skippedLayerNames?.length ? (
        <Alert severity="warning">
          This file contains multiple layers. Only the first layer ({gdalInfo.layerName}) is shown.
          Skipped: {gdalInfo.skippedLayerNames.join(', ')}.
        </Alert>
      ) : null}

      {gdalSkipped && (
        <Alert severity="info">
          This file exceeds the 2.5 GB limit for browser-side attribute extraction. Attribute
          configuration is unavailable.
          {/* @TODO: Let them build based off of what they know is in the file anyway, using a row builder */}
        </Alert>
      )}

      {gdalFailed && (
        <Alert severity="warning">
          Attribute extraction failed — the file format may not be supported. Attribute
          configuration is unavailable.
        </Alert>
      )}

      {configs.length > 0 && (
        <>
          <Typography variant="subtitle2">
            Configure Element Attributes ({includedCount} of {configs.length} included)
          </Typography>

          <Stack spacing={1}>
            {configs.map((config) => (
              <FieldConfigRow
                key={config.fieldName}
                config={config}
                category={getTypeCategory(config.gdalType)}
                onChange={(updates) => updateConfig(config.fieldName, updates)}
              />
            ))}
          </Stack>

          <Divider />

          <Box>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowJson((v) => !v)}
              sx={{ mb: 1 }}
            >
              {showJson ? 'Hide' : 'Preview'} generated config
            </Button>
            {showJson && (
              <Box
                component="pre"
                sx={{
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  p: 2,
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 400,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {generatedJson}
              </Box>
            )}
          </Box>
        </>
      )}

      <Stack direction="row" spacing={1}>
        {configs.length > 0 && (
          <Button variant="contained" onClick={handleUploadConfig} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload config'}
          </Button>
        )}
        <Button variant="outlined" onClick={onReset}>
          Upload another
        </Button>
      </Stack>
    </Box>
  )
}

export default ElementAttributesStep
